import { Section, Container } from '@/components/layout';
import { formatDateRangeHero, type TourEvent } from '@/features/events';
import { ResponsiveImage } from '@/components/media';
import { DividerCorazon, HilosHero, OndaCorazon, SelloMedallon } from '../icons/BogotaIcons';
import banderaCo from '@/assets/icons/flags/co.svg';
import { MigaGira } from '@/pages/shared/MigaGira';
import styles from './HeroBogota.module.css';
import { HeroReservaCta } from '@/pages/shared/ConversionBlocks';
import { VslInline } from '@/pages/shared/VslInline';

/* =============================================================================
 * S1 · HERO DE BOGOTÁ  (mockup y 0–637 de 1536 · 41,5 % del alto)
 * -----------------------------------------------------------------------------
 * Tres capas, como el mockup: paisaje de montaña con la iglesia blanca A LA
 * DERECHA, retrato de Azrahel recortado A LA IZQUIERDA, y el bloque de texto
 * sobre el valle (la zona limpia y luminosa de la foto).
 *
 * ERRATAS DEL MOCKUP QUE AQUÍ SE CORRIGEN (ADR-003 §5, CLAUDE.md §7):
 *   · «Sanacion» → «Sanación».
 *   · El rótulo del logo dice sin omega: se usa el LOGO OFICIAL en PNG (ADR-002),
 *     nunca se teclea la marca sin omega.
 *
 * El titular es TEXTO REAL seleccionable, nunca una imagen con letras dentro.
 * ========================================================================== */
export function HeroBogota({ event }: { event: TourEvent }) {
  return (
    <Section
      width="full"
      spacing="flush"
      background="none"
      labelledBy="bogota-hero-titulo"
      className={styles.section}
    >
      <div className={styles.hero}>
        {/* Vuelta al hub sin abrir el menú, a todos los anchos (encargo del
            cliente, 25-ago-2026: «en algunas landing se muestra lo de las migas
            de pan, y en otras no»). Sustituye a `MigaGiraBogota`, que era una
            tira en flujo y se escondía de 1024 arriba. */}
        <MigaGira event={event} className={styles.miga} />

        {/* --- Capa 1 · paisaje a sangre ------------------------------------ */}
        <div className={styles.media}>
          <ResponsiveImage
            asset="bogota/hero"
            className={styles.landscape}
            sizes="100vw"
            loading="eager"
            fetchPriority="high"
            decorative
          />
          {/* --- Capa 2 · retrato recortado, sangra por izquierda e inferior -- */}
          <ResponsiveImage
            asset="azrahel/recorte"
            className={styles.portrait}
            sizes="(min-width: 60rem) 38vw, 62vw"
            loading="eager"
            fetchPriority="high"
            alt="Azrahel del Mayor"
          />
          <span className={styles.veil} aria-hidden="true" />
        </div>

        <HilosHero className={styles.threads} />

        {/* --- Capa 3 · bloque de texto ------------------------------------- */}
        <Container width="wide" className={styles.contentWrap}>
          {/* ⚠️ EL HERO NO LLEVA `Reveal`, Y ES DELIBERADO.
              `Reveal` se dispara con `whileInView`, es decir, AL ENTRAR en el
              viewport haciendo scroll. Un hero está sobre el pliegue por
              definición: no hay entrada que esperar. Medido en navegador real
              con movimiento normal, el `<h1>` cargaba con OPACIDAD 0 y sólo
              aparecía al mover la rueda — la página abría en blanco.
              (Mismo defecto que el ORACLE vio en el hero de Cancún.)
              El movimiento de entrada se reserva para lo que está por debajo
              del pliegue, que es para lo que sirve. */}
          <div className={styles.contentReveal}>
            <div className={styles.content}>
              {/* SELLO SUPERIOR. El mockup pone un medallón de 161x163 (aro doble de
                  trazo fino + corazón encaramado + dos abanicos de rayos) con el rótulo
                  de marca dentro. El ARO es ornamento y se calca en SVG; el RÓTULO es
                  la marca y va con el LOGO OFICIAL en PNG (ADR-002): el póster escribe
                  «AOGR» sin omega y eso es una errata de la generación de imagen, no un
                  diseño. La marca es AΩGR / ÁGORA y aquí no se teclea nunca. */}
              <span className={styles.seal}>
                <SelloMedallon className={styles.sealRing} />
                <ResponsiveImage
                  asset="marca/lockup"
                  className={styles.logo}
                  sizes="(min-width: 60rem) 104px, 76px"
                  loading="eager"
                  fetchPriority="high"
                  alt="AΩGR (ÁGORA) · Escuela Pleyadiana"
                />
              </span>

              <p className={styles.city}>
                <span>Bogotá</span>
                <img
                  className={styles.flag}
                  src={banderaCo}
                  alt=""
                  width={30}
                  height={30}
                  aria-hidden="true"
                />
              </p>

              <p className={styles.dates}>
                <time dateTime={event.startDate}>{formatDateRangeHero(event)}</time>
              </p>

              <h1 id="bogota-hero-titulo" className={styles.title}>
                {event.titleMain}
              </h1>

              {/* EL VSL, ENCIMA DE LA PROMESA. Encargo del ORACLE (12-sep-2026): «debería
                  ir arriba del texto rompe el motor». Y va como HERMANO del texto, no
                  colgando del botón de compra: ahí dentro heredaba el ancho de un flex
                  que se encoge a su contenido y el vídeo salía a 290 px en Buenos Aires
                  y 478 en Bogotá —un sello de correos, y distinto en cada ciudad—.
                  Aquí coge el ancho del bloque de texto, el del titular. */}
              <VslInline event={event} />

              <DividerCorazon className={styles.divider} />

              <p className={styles.promise}>
                {event.promiseLines.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </p>

              {/* El botón de compra colgando de la promesa, igual que en las
                  otras cuatro (encargo del cliente, 28-jul-2026). */}
              <HeroReservaCta event={event} />
            </div>
          </div>
        </Container>

        <OndaCorazon className={styles.wave} />
      </div>
    </Section>
  );
}
